import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Link,
    IconButton,
    Button,
    useTheme,
    useMediaQuery,
    Divider,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Facebook,
    Twitter,
    Instagram,
    LinkedIn,
    LocationOn,
    Phone,
    Email,
    Restaurant,
    Download,
    WhatsApp,
    ArrowForward,
} from '@mui/icons-material';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import api from '../../services/api';
import menuCardService from '../../services/menuCardService';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { restaurantService } from '../../services/restaurantService';
import { useNotification } from '../../contexts/NotificationContext';
import outletService from '../../services/outletService';
import BusinessHoursDisplay from '../common/BusineesHours'
import tiktok1 from '../../static/tiktok1.png'
import colors from "../colors";
import { color } from 'framer-motion';

const footerColor = {
    bg: colors.primary.main,
    text: '#FFFFF0',
    white: '#FFFFF0',
    hover: colors.secondary.main
}

// Standard font sizes for consistency
const textSizes = {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    xxl: '1.5rem',    // 24px
}

const Footer = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showSuccess, showError } = useNotification();
    const {
        selectedOutlet,
        menuItems,
        categories,
        loading: restaurantLoading
    } = useRestaurant();

    const [restaurantData, setRestaurantData] = useState(null);
    const [outletData, setOutletData] = useState([])
    const [socialLinks, setSocialLinks] = useState({
        facebook: '',
        instagram: '',
        whatsapp: '',
        tiktok: '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingPDF, setDownloadingPDF] = useState(false);

    const currentYear = new Date().getFullYear();


    const [quickLinks, setQuickLinks] = useState([
        { 'label': "Home", "link": "/" },
        { 'label': "Menu", "link": "/delivery" },
        { 'label': "About Us", "link": "/about" },
        { 'label': "Order Online", "link": "/delivery" },
        { 'label': "Reservation", "link": "/table-reservations" },
        { 'label': "Takeaway", "link": "/delivery" },
        { 'label': "Contact Us", "link": "/contact" },
        // { 'label': "Branches", "link": "/" },
    ])

    useEffect(() => {
        const fetchRestaurantData = async () => {
            try {
                setLoading(true);
                const [restaurantResponse, socialResponse, outletResponse] = await Promise.all([
                    api.get('/public/restaurant'),
                    api.get('/public/social-links').catch(() => ({ data: null })),
                    restaurantService.getPublicOutlets()
                ]);
                setRestaurantData(restaurantResponse.data);
                setOutletData(outletResponse);
                if (socialResponse.data) {
                    setSocialLinks(socialResponse.data);
                }
                setError(null);
            } catch (err) {
                console.error('Error fetching restaurant data:', err);
                setError('Failed to load restaurant information');
                setRestaurantData({
                    name: 'Restaurant Name',
                    description: 'Authentic Indian cuisine served with love and tradition. Experience the rich flavors of India in every bite.',
                    phone: '+1 (555) 123-4567',
                    email: 'info@restaurant.com',
                    website: null
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRestaurantData();
    }, []);

    const handleDownloadMenuPDF = async () => {
        try {
            setDownloadingPDF(true);
            if (!selectedOutlet) {
                showError('Please select an outlet first');
                return;
            }
            if (!menuItems || menuItems.length === 0) {
                showError('No menu items available for download');
                return;
            }

            const pdfBlob = await menuCardService.generateMenuCardPDF(
                selectedOutlet,
                menuItems,
                categories
            );

            const filename = `Menu_${selectedOutlet.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            menuCardService.downloadPDF(pdfBlob, filename);

            showSuccess('Menu PDF downloaded successfully!');
        } catch (error) {
            console.error('Error downloading menu PDF:', error);
            showError(`Failed to download menu PDF: ${error.message}`);
        } finally {
            setDownloadingPDF(false);
        }
    };

    return (
        <>
            <Box
                component="footer"
                sx={{
                    width: '100%',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : footerColor['bg'],
                    color: theme.palette.mode === 'dark' ? 'grey.100' : footerColor['text'],
                    mt: 'auto',
                    pt: { xs: 3, md: 4 },
                    boxShadow: '0 -4px 8px rgba(0,0,0,0.1)',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Container sx={{ px: { xs: 2, sm: 3, md: 4 }, }}>
                    {/* Main Footer Content */}
                    <Box sx={{ maxWidth: '1440px', margin: { md: '0 auto' } }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'column', md: 'row' }, flexWrap: 'wrap', gap: 2 }}>
                            {/* logo and description */}
                            <Box sx={{ width: { md: '32%' }, textAlign: 'center', borderBottom: { xs: '3px solid white', sm: '3px solid white', md: '2px solid white' }, mb: 2 }}>
                                {restaurantData?.logo_url && <Box component="img"
                                    src={restaurantData?.logo_url}
                                    sx={{
                                        width: '120px',
                                        height: '120px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                /> }
                                <Box>
                                    <Typography sx={{
                                        px: { xs: 1, sm: 1, md: 8 },
                                        pb: 1,

                                    }}>
                                        {restaurantData?.description}
                                    </Typography>
                                </Box>

                            </Box>


                            <Box sx={{ width: { md: '13%' } }}>
                                <Typography sx={{
                                    fontSize: '20px !important',
                                    fontFamily: 'playfair Display !important',
                                    color: colors.primary.w_text,
                                    mb: 1,
                                    textAlign: { xs: 'left', sm: 'left', md: 'left' },
                                    ml: { xs: 2, sm: 2 }
                                }}>Quick Links</Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    alignItems: { xs: 'flex-start', md: 'flex-start' },
                                    ml: { xs: 2, sm: 2 }
                                }}>
                                    {quickLinks.map((link, index) => (
                                        <Link
                                            key={index}
                                            href={link.link}
                                            color="inherit"
                                            underline="hover"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: { xs: textSizes.xs, md: '16px !important' },
                                                '&:hover': { color: '#ECD73C' }
                                            }}
                                        >
                                            <ArrowForward sx={{ mr: 1, fontSize: { xs: '14px', md: '20px !important' } }} />
                                            {link.label}
                                        </Link>
                                    ))}
                                </Box>
                            </Box>
                            <Box sx={{ width: { md: '45%' } }}>
                                <Typography sx={{
                                    fontSize: '20px !important',
                                    fontFamily: 'playfair Display !important',
                                    color:  colors.primary.w_text,
                                    mb: 1,
                                    textAlign: { xs: 'left', sm: 'left' },
                                    ml: { xs: 2, sm: 2 }
                                }}>Contact Us</Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ width: { sm: '100%' }, display: 'flex', flexWrap: 'wrap' }}>
                                        {outletData && outletData.map((outlet, index) => {
                                            const countryCode = outlet?.country_code;
                                            const phone = outlet?.phone;

                                            const whatsappCode = outlet?.whatsapp_country_code
                                            const whatsapp = outlet?.whatsapp;


                                            const formattedWhatsapp =
                                                whatsapp && whatsappCode
                                                    ? `+${whatsappCode} ${whatsapp.slice(whatsappCode.length)}`
                                                    : '';


                                            const formattedPhone =
                                                phone && countryCode
                                                    ? `+${countryCode} ${phone.slice(countryCode.length)}`
                                                    : '';
                                            return (
                                                <Box sx={{ width: { md: '40%' } }}>
                                                    <Box sx={{ p: 2 }}>
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography sx={{ fontSize: { md: '16px !important' }, fontWeight: 500 }}>{outlet?.name}</Typography>
                                                            <Typography sx={{ fontSize: { md: '14px !important' } }}>{outlet?.address}</Typography>
                                                            <Typography sx={{ fontSize: { md: '14px !important' }, mb: 2 }}>{outlet?.state} - {outlet?.pincode}</Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                                            >
                                                                <Phone sx={{ color: colors.primary.w_text, fontSize: { md: '14px !important' }, mr: 0.5 }} />
                                                                {outlet?.phone}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <WhatsApp sx={{ color: colors.primary.w_text, fontSize: { md: '14px !important' }, mr: 0.5 }} />
                                                                {formattedWhatsapp}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontSize: { md: '14px !important' }, mb: 2 }}>Timing:</Typography>
                                                        <BusinessHoursDisplay business_hours={outlet.business_hours} compColor={true} />
                                                        {/* <Typography sx={{ fontSize: {md:'14px !important'},mb:1 }}>Lunch : 11:00AM to 03:00PM</Typography>
                                                    <Typography sx={{ fontSize: {md:'14px !important'} }}>Dinner : 05:00PM to 11:00PM</Typography> */}
                                                    </Box>

                                                </Box>
                                            )
                                        })}
                                    </Box>

                                    <Box>
                                        <Typography sx={{
                                            fontSize: '20px !important',
                                            fontFamily: 'playfair Display !important',
                                            color:  colors.primary.w_text,
                                            mb: 1,
                                            ml: { xs: 2, sm: 2 }
                                        }}>Need Help ?</Typography>
                                        <Box sx={{ p: 2 }}>
                                            <Typography sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Phone sx={{ color: colors.primary.w_text, fontSize: '14px !important', mr: 0.5 }} />
                                                {restaurantData?.phone}</Typography>
                                            {restaurantData?.email &&
                                                <Typography>
                                                    <Email sx={{ color: colors.primary.w_text, fontSize: '14px !important', mr: 0.5 }} />
                                                    {restaurantData?.email}</Typography>
                                            }
                                        </Box>
                                    </Box>
                                </Box>

                            </Box>


                        </Box>

                        <Box sx={{ width: { md: '32%' }, textAlign: 'center', overflow: 'hidden', mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, px: 2 }}>
                                {socialLinks.facebook && (
                                    <IconButton
                                        href={socialLinks.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            bgcolor: '#ECD73C',
                                            color: '#800000',
                                            '&:hover': {
                                                bgcolor: footerColor['white'],
                                                // transform: 'scale(1.1)'
                                            },
                                            fontSize: { xs: 'small', md: 'medium' }
                                        }}
                                    >
                                        <Facebook />
                                    </IconButton>
                                )}
                                {socialLinks.instagram && (
                                    <IconButton
                                        href={socialLinks.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            bgcolor: '#ECD73C',
                                            color: '#800000',
                                            '&:hover': {
                                                bgcolor: footerColor['white'],
                                                // transform: 'scale(1.1)'
                                            },
                                            fontSize: { xs: 'small', md: 'medium' }
                                        }}
                                    >
                                        <Instagram />
                                    </IconButton>
                                )}
                                {/* {socialLinks.whatsapp && (
                                    <IconButton
                                        href={socialLinks.whatsapp}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            bgcolor: '#ECD73C',
                                            color: '#800000',
                                            '&:hover': {
                                                bgcolor: footerColor['white'],
                                                // transform: 'scale(1.1)'
                                            },
                                            fontSize: { xs: 'small', md: 'medium' }
                                        }}
                                    >
                                        <WhatsApp />
                                    </IconButton>
                                )} */}
                                {socialLinks.tiktok && (
                                    <IconButton
                                        href={socialLinks.tiktok}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            bgcolor: '#ECD73C',
                                            color: '#800000',
                                            '&:hover': {
                                                bgcolor: footerColor['white'],
                                            },
                                            fontSize: { xs: 'small', md: 'medium' }
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={tiktok1}
                                            alt="TikTok"
                                            sx={{
                                                width: 22,
                                                height: 22,
                                            }}
                                        />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                    </Box>

                    {/* Divider */}
                    {/* <Divider
                    sx={{
                        maxWidth: '1440px',
                        margin: { md: '0 auto' },
                        my: 5,
                        height: "2px",
                        border: "none",
                        background: "linear-gradient(90deg, #891313, #ECD73C, #891313)"
                    }}
                /> */}

                    {/* Copyright & Links */}
                    {/* <Box
                    sx={{
                        maxWidth: '1440px',
                        margin: { md: '0 auto' },
                        display: 'flex',
                        pt: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: { xs: textSizes.xs, md: textSizes.sm },
                            order: { xs: 2, sm: 1 }
                        }}
                    >
                        © {currentYear} {restaurantData?.name || 'Restaurant Name'}. All rights reserved.
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        gap: { xs: 2, md: 3 },
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        order: { xs: 1, sm: 2 }
                    }}>
                        {['About Us', 'Contact Us', 'Privacy Policy', 'Terms of Service'].map((link, index) => (
                            <Link
                                key={index}
                                href={`/${link.toLowerCase().replace(' ', '-')}`}
                                color="inherit"
                                underline="hover"
                                variant="body2"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    fontSize: { xs: textSizes.xs, md: textSizes.sm },
                                    '&:hover': { color: '#ECD73C' }
                                }}
                            >
                                {link}
                            </Link>
                        ))}
                    </Box>
                </Box> */}


                </Container>
                <Box sx={{ backgroundColor: colors.primary.main, textAlign: 'center', width: '100%', p: 2, height: '100%' }}>
                    <Typography sx={{ color: 'white' }}>{restaurantData?.name} |  © {currentYear} . All Copyrights rights reserved by {restaurantData?.name} | Privacy Policy</Typography>
                </Box>

            </Box>

        </>
    );
};

export default Footer;